import React, { Suspense, useState, useRef, useEffect } from "react";

/**
 * Higher-order component that adds lazy loading functionality to any component
 * @param {React.ComponentType} Component - The component to be lazy loaded
 * @param {React.ComponentType} LoadingComponent - The component to show while loading
 * @param {Object} options - Configuration options for the IntersectionObserver
 * @returns {React.ComponentType} - The wrapped component with lazy loading
 */
const withLazyLoading = (
  Component,
  LoadingComponent,
  options = {
    rootMargin: "200px 0px",
    threshold: 0
  }
) => {
  return function WithLazyLoadingComponent(props) {
    const [shouldLoad, setShouldLoad] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isResourceLoaded, setIsResourceLoaded] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            setIsVisible(true);
            observer.disconnect();
          }
        },
        {
          root: null,
          ...options
        }
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }, []);

    const componentProps = {
      ...props,
      isVisible,
      onLoaded: () => {
        setIsResourceLoaded(true);
      }
    };

    return (
      <div ref={containerRef}>
        {shouldLoad ? (
          <Suspense fallback={<LoadingComponent />}>
            <div style={{ display: isResourceLoaded ? "block" : "none" }}>
              <Component {...componentProps} />
            </div>
            {!isResourceLoaded && <LoadingComponent />}
          </Suspense>
        ) : (
          <LoadingComponent />
        )}
      </div>
    );
  };
};

export default withLazyLoading;
